import { S, t, type Locale } from '../i18n/strings';
import { AUTHORS } from './authors';

/**
 * Steve dans la console du navigateur.
 *
 * Le quatrième easter egg, et le seul qui ne se déclenche pas au clavier : il
 * n'y a pas de mot secret à taper, parce qu'il n'y aurait aucun moyen de le
 * deviner depuis un panneau que le site ne contrôle pas. Il s'affiche donc
 * **une fois par chargement**, et attend qu'on ouvre les outils de développement.
 *
 * Pourquoi une image et pas de l'ASCII : la seule chose qui rend Steve
 * reconnaissable, c'est qu'il est orange. Un aplat de dièses ne le dira jamais.
 * `console.log('%c …')` accepte `background-image`, et la CSP du site autorise
 * déjà `data:` pour les images (`img-src 'self' data: blob:`) — cet œuf n'a donc
 * demandé aucun assouplissement de la politique de sécurité.
 *
 * Le module est pur : il **fabrique** les arguments, il ne les envoie pas. C'est
 * ce qui permet de les vérifier sans console et sans navigateur.
 */

/**
 * Steve en SVG, aux mêmes coordonnées que `Steve.module.css` — boîte de
 * 300 × 210, corps de 236 × 134, œil de 44 px à (184, 70), bras en capsules
 * pivotées de 42° et −40°.
 *
 * L'ordre des formes porte la profondeur, comme dans le composant : le bras
 * arrière passe **avant** le corps, seul son bout dépasse. Et la queue est un
 * triangle dont le bord plat est à gauche — sa pointe unique rentre dans le
 * corps, ce n'est pas la fourche d'un poisson ordinaire.
 */
const STEVE_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="-40 14 280 194" width="280" height="194">',
  '<rect x="14" y="112" width="60" height="14" rx="7" fill="#c2410c" transform="rotate(-40 74 119)"/>',
  '<polygon points="-38,50 -38,122 18,86" fill="#f97316"/>',
  '<ellipse cx="118" cy="85" rx="118" ry="67" fill="#f97316"/>',
  '<circle cx="184" cy="70" r="22" fill="#fff"/>',
  '<circle cx="184" cy="70" r="10" fill="#04161d"/>',
  '<rect x="150" y="112" width="66" height="15" rx="8" fill="#f97316" transform="rotate(42 150 119.5)"/>',
  '<rect x="68" y="144" width="17" height="54" rx="4" fill="#f97316"/>',
  '<rect x="128" y="144" width="17" height="54" rx="4" fill="#f97316"/>',
  '<rect x="56" y="192" width="38" height="12" rx="6" fill="#c2410c"/>',
  '<rect x="118" y="192" width="38" height="12" rx="6" fill="#c2410c"/>',
  '</svg>',
].join('');

/**
 * Taille du dessin dans la console, en pixels.
 *
 * Les deux sont pairs : le remplissage vaut la moitié de chaque côté, et une
 * demi-pixel n'aurait servi à rien. Le rapport reste celui du SVG à un cheveu
 * près, et `contain` s'occupe du reste.
 */
const DRAWN = { width: 224, height: 156 } as const;

/**
 * Le SVG en URI de données.
 *
 * Encodé en base64 plutôt qu'en pourcents : la chaîne contient des `#`, qui
 * couperaient l'URL au premier dièse si elle était laissée telle quelle. Et
 * `btoa` suffit — le dessin est en ASCII pur, il n'a aucun caractère à échapper.
 */
export function steveDataUri(encode: (raw: string) => string = btoa): string {
  return `data:image/svg+xml;base64,${encode(STEVE_SVG)}`;
}

/**
 * Le style du premier message : une boîte vide, remplie par le dessin.
 *
 * `font-size: 0` supprime la ligne de texte qui porterait la boîte, et le
 * remplissage lui donne sa taille — c'est la seule façon d'obtenir une image
 * dans une console, qui ne sait afficher que du texte stylé.
 */
export function steveBackdrop(uri: string): string {
  return [
    'font-size: 0',
    `padding: ${String(DRAWN.height / 2)}px ${String(DRAWN.width / 2)}px`,
    `background: url("${uri}") no-repeat center / contain`,
  ].join('; ');
}

/** Un appel à `console.log`, prêt à être passé tel quel. */
export type ConsoleCall = readonly [message: string, ...styles: string[]];

/**
 * Les deux appels de l'œuf, dans l'ordre.
 *
 * Deux, et pas un seul : Safari ignore largement le style `%c`, et n'afficherait
 * alors qu'une ligne vide. La seconde ligne est du texte ordinaire — les
 * crédits et les adresses des auteurs — donc elle arrive partout, et c'est celle
 * qui compte le plus. Le dessin est le bonus.
 */
export function consoleEgg(lang: Locale, uri: string = steveDataUri()): readonly ConsoleCall[] {
  const credit = `${t(S.common.credit_idea, lang)} Vigz — ${AUTHORS.Vigz}`;
  const music = `${t(S.common.credit_music, lang)} Tomo — ${AUTHORS.Tomo}`;

  return [
    ['%c ', steveBackdrop(uri)],
    [`%c${t(S.common.title, lang)}%c\n${credit}\n${music}\n${t(S.common.credit_unofficial, lang)}`,
      'color: #f97316; font-weight: 700; font-size: 15px',
      'color: inherit; font-weight: 400; font-size: inherit'],
  ];
}

/**
 * Envoie l'œuf, une seule fois par chargement.
 *
 * Le garde n'est pas un luxe : en développement, React monte deux fois en mode
 * strict, et la console se retrouverait avec deux Steve.
 */
let printed = false;

export function printConsoleEgg(lang: Locale, out: Pick<Console, 'log'> = console): void {
  if (printed) return;
  printed = true;
  try {
    for (const [message, ...styles] of consoleEgg(lang)) out.log(message, ...styles);
  } catch {
    /* Une console indisponible ou bridée ne doit jamais faire tomber le site. */
  }
}

/** Réarme l'œuf. Réservé aux tests : le site ne l'appelle jamais. */
export function resetConsoleEgg(): void {
  printed = false;
}
